import { useState, type FC } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import usePenetapanTahunanDinas from 'app/KinerjaDinasPage/hooks/usePenetapanTahunanDinas'
import { Header } from 'app/KinerjaShared/components/Header'
import { PANDUAN_PENGELOLAAN_DINAS_URL } from 'app/KinerjaShared/utils/panduan'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import useTabKsGuru from 'app/KinerjaShared/hooks/useTabKsGuru'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import { FilterSearchStatusPengiriman } from 'app/KinerjaShared/components/FilterSearchStatusPengiriman'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import CardSummaryPenetapanTahunan from 'app/KinerjaDinasPage/components/CardSummaryPenetapanTahunan'
import usePenetapanTahunanCountDinas from 'app/KinerjaDinasPage/hooks/usePenetapanTahunanCountDinas'
import DialogPredikatKinerjaTahunanUpdate from 'app/KinerjaDinasPage/components/DialogPredikatKinerjaTahunanUpdate'
import { DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED } from 'app/KinerjaDinasPage/constants'
import { NotFoundError } from 'utils/customError'
import RowPredikatKinerjaPenetapanTahunan from 'app/KinerjaDinasPage/components/RowPredikatKinerjaPenetapanTahunan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type {
  TPenilaianTahunan,
  TPenilaianTahunanStatePengiriman,
  TPenilaianTahunanStatePengirimanComplete,
} from 'types/kinerja-dinas-penilaian'

type TProps = {}

const hideTabGuru = !DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED

const COLUMNS_COUNT = 7

const DinasPenetapanTahunanIndex: FC<TProps> = ({}) => {
  const router = useRouter()

  const { periodYear } = usePeriodContext()

  const [dialogOpen, setDialogOpen] = useState<
    'info-predikat-kinerja' | 'update-predikat-kinerja' | null
  >(null)
  const [selectedItem, setSelectedItem] = useState<TPenilaianTahunan>(null)
  const [activeParams, setActiveParams] = useState<
    TPenilaianTahunanStatePengirimanComplete[]
  >(['ALL'])

  // Tabs
  const { render: tabs, activeTabName } = useTabKsGuru({
    hideTabGuru,
  })

  const {
    // Common
    isBothFetched,
    isWithinAllowedRange,
    isClickable,

    // Combined
    isLoading: isCountLoading,
  } = usePenetapanTahunanCountDinas()

  const {
    // Kepala Sekolah
    metasKs,
    refetchListKs,

    // Guru
    metasGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    hasItems,

    // Relative to active tab
    relativeItems,
    isRelativeError,
    isRelativeSuccess,
  } = usePenetapanTahunanDinas({
    year: periodYear,
    isAccessible: isWithinAllowedRange,
    activeTabName,
    statusPengiriman: activeParams || undefined,
  })

  const isLoading = isCountLoading || isListLoading

  if (isBothFetched && (!isWithinAllowedRange || !isClickable))
    throw new NotFoundError('', 'Not Found')

  return (
    <div>
      <Header
        title="Penetapan Predikat Kinerja Pegawai (Tahunan)"
        className="mb-9 h-14 print:!hidden"
        rightNode={
          <div className="bg-white rounded">
            <Button
              as="a"
              variant="ghost"
              rel="noopener noreferrer"
              className="flex gap-1.5 h-14"
              size="md"
              href={PANDUAN_PENGELOLAAN_DINAS_URL}
              target="_blank"
            >
              <Icon fontSize="sm" color="informational">
                help
              </Icon>
              Baca panduan
            </Button>
          </div>
        }
      />

      <section className="bg-white border border-subdued rounded-lg mt-6">
        <div className="px-6 pt-6">
          {/* Title */}
          <div className="space-y-1">
            <Text variant="heading-md">
              Daftar Penetapan Predikat Kinerja Pegawai (Tahunan)
            </Text>
            <Text color="subdued">
              Data mungkin muncul secara berkala. Anda dapat ubah rekomendasi
              jika perlu.
            </Text>
          </div>

          {/* Summary */}
          <CardSummaryPenetapanTahunan
            year={periodYear}
            onClickSend={() => {
              router.push(
                `${PENGELOLAAN_KINERJA_KADIN_PATH}/penetapan-tahunan/konfirmasi`
              )
            }}
          />
        </div>

        {/* Tabs */}
        {!hideTabGuru && <>{tabs}</>}

        {/* Filter & Search */}
        <FilterSearchStatusPengiriman
          hideSearch
          hideBelumDinilai
          className="px-5 my-5"
          isLoading={isLoading}
          data={{
            ...(activeTabName === 'GURU' ? metasGuru?.counts : metasKs?.counts),
            belumDinilai: 0,
          }}
          onChange={({ filter }) => {
            setActiveParams(filter as TPenilaianTahunanStatePengiriman[])
          }}
        />

        {/* Table */}
        <Table className={clsx({ 'mt-0': !hideTabGuru })}>
          <TableHeader>
            <TableHeaderItem className="w-2/12">
              <Text variant="body" color="default" className="!font-bold">
                Nama
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Predikat Kinerja
                <br />
                Januari-Juni
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Predikat Kinerja
                <br />
                Juli-Desember
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Rekomendasi
                <br />
                Predikat Kinerja
                <br />
                (Tahunan)
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Perubahan Predikat
                <br />
                Kinerja (Tahunan)
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-1/12 pl-0">
              <Text variant="body" color="default" className="!font-bold">
                Status
                <br />
                Pengiriman
              </Text>
            </TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0" />
          </TableHeader>
          <TableBody>
            {/* If loading */}
            {isListLoading && <TableShimmer columns={COLUMNS_COUNT} />}

            {/* If error or no data returned */}
            {!isLoading && (isRelativeError || relativeItems?.length === 0) && (
              <TableRowStates
                colSpan={COLUMNS_COUNT}
                type={isRelativeError ? 'ERROR' : 'EMPTY'}
                onClick={() => {
                  if (activeTabName === 'GURU') refetchListGuru()
                  else refetchListKs()
                }}
              >
                <>
                  Pastikan terdapat pegawai dengan jabatan tersebut di daerah
                  <br />
                  Anda dan sudah menyelesaikan tahap Pelaksanaan Kinerja.
                </>
              </TableRowStates>
            )}

            {!isLoading && isRelativeSuccess && hasItems && (
              <RowPredikatKinerjaPenetapanTahunan
                items={relativeItems}
                onSelect={(item) => {
                  setDialogOpen('update-predikat-kinerja')
                  setSelectedItem(item)
                }}
              />
            )}
          </TableBody>
        </Table>
      </section>

      <DialogPredikatKinerjaTahunanUpdate
        activeTabName={activeTabName}
        year={periodYear}
        data={selectedItem}
        isOpen={dialogOpen === 'update-predikat-kinerja'}
        onClose={() => {
          setSelectedItem(null)
          setDialogOpen(null)
        }}
      />
    </div>
  )
}

export default DinasPenetapanTahunanIndex
